import React, { useEffect, useRef, useState } from 'react';
import { isEmpty, isNull, map, take } from 'lodash-es';
import cN from 'classnames';
import { Drawer, Input, InputRef } from 'antd';
import { SourcingMarketplaceSearchType, ViewType } from '@/shared/types/sourcing-marketplace';
import { useQueryParams, useRequest } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { logCommonView } from '@/lib/service';
import { EventSource } from '@/shared/constants/event';
import { AllCategoryMap, CatalogCompany } from '@/shared/types/catalog';
import Link from 'next/link';
import { AutoCompleteSupplier, SearchAutoCompleteResponse } from '@/shared/types/api/catalog';
import { getSearchAutoComplete } from '@/lib/service/catalog';
import { getSupplierName } from '@/shared/utils/sourcing-marketplace-util';
import './index.less';
import FilterIcon from '@/components/Common/Icons/FilterIcon';
import { useConfigProvider } from '@/lib/context';
import { useRouter } from 'next/router';
import Image from 'next/image';

interface IProps {
  searchType: SourcingMarketplaceSearchType;
  placeholderText: string;
  topRankedSearchTerms: string[];
  viewType?: ViewType;
  defaultPopularSuppliers?: CatalogCompany[];
  labelText?: string;
  ifShowMobileFilterIcon?: boolean;
  onMobileFilterIconClick?: () => void;
}

const SourcingMarketplaceSearch = ({
  searchType,
  placeholderText,
  topRankedSearchTerms,
  viewType,
  defaultPopularSuppliers,
  labelText,
  ifShowMobileFilterIcon,
  onMobileFilterIconClick,
}: IProps) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [currentSearchText, setCurrentSearchText] = useState<string>('');
  const [autoCompleteResults, setAutoCompleteResults] = useState<SearchAutoCompleteResponse>();

  const { isMobile } = useConfigProvider();
  const inputRef = useRef<InputRef>(null);
  const { queryParams, setQueryParams } = useQueryParams<{}>();
  const searchKey = queryParams.get(ParamKey.SEARCH);
  const router = useRouter();

  useEffect(() => {
    if (isEmpty(searchKey)) {
      setCurrentSearchText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  useEffect(() => {
    setCurrentSearchText(searchKey);

    if (searchKey) {
      getAutoComplete({
        text: searchKey,
        size: 5,
        includeCompanySuggests: true,
      });
    } else {
      setAutoCompleteResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  const { run: getAutoComplete } = useRequest(getSearchAutoComplete, {
    manual: true,
    onSuccess: (response: SearchAutoCompleteResponse) => {
      setAutoCompleteResults(response);
    },
  });

  /** Handlers */
  const handleOnInputChange = (text: string) => {
    setCurrentSearchText(text);
    if (isEmpty(text)) {
      setAutoCompleteResults(null);
    } else {
      getAutoComplete({
        text: text,
        size: 5,
        includeCompanySuggests: true,
      });
    }
  };

  const updateSearchQueryParam = (text: string) => {
    setQueryParams({ [ParamKey.SEARCH]: text });
  };

  const handleClickSearchTerm = (text: string) => {
    logCommonView(EventSource.BROWSER_SUPPLIER_CLICK_SEARCH_TERM, text);
    setCurrentSearchText(text);

    logCommonView(EventSource.BROWSER_SUPPLIER_SEARCH, text);
    setIsDropdownVisible(false);
    updateSearchQueryParam(text);
  };

  const handleSearch = (text: string) => {
    logCommonView(EventSource.BROWSER_SUPPLIER_SEARCH, text);
    setIsDropdownVisible(false);

    if (searchType === SourcingMarketplaceSearchType.HOMEPAGE) {
      // Go to FDL
      router.push({
        pathname: '/sourcing/browse/all/suppliers',
        query: { [ParamKey.SEARCH]: text },
      });
    } else {
      updateSearchQueryParam(text);
    }
  };

  const handleClearSearch = () => {
    updateSearchQueryParam(null);
    setCurrentSearchText('');
    setAutoCompleteResults(null);
    setIsDropdownVisible(false);
  };

  const handleClickInputField = () => {
    if (isEmpty(searchKey)) {
      setAutoCompleteResults(null);
    }
    setIsDropdownVisible(true);
  };

  /** Rendering */
  const searchTermsInnerEl = (items) => {
    return (
      <div className="search-terms">
        {map(items, (text: string, i: number) => {
          return (
            <div key={`${text}-${i}`} className="search-term" onClick={(event) => handleClickSearchTerm(text)}>
              <span className="term">{text}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const suppliersInnerEl = (items: CatalogCompany[] | AutoCompleteSupplier[]) => {
    return (
      <div className="search-terms">
        {map(items, (supplier: CatalogCompany) => {
          const supplierName = getSupplierName(supplier);

          return (
            <Link
              key={supplier.id}
              className="search-term"
              href={`/sourcing/suppliers/${supplier.handle}`}
              onClick={() => {
                logCommonView(EventSource.BROWSER_SUPPLIER_CLICK_SEARCH_TERM_SUPPLIER, supplier.id);
                setIsDropdownVisible(false);
              }}
            >
              {supplier?.avatarUrl && (
                <Image
                  className="supplier-image"
                  src={supplier.avatarUrl}
                  width={22}
                  height={22}
                  alt={supplierName}
                  loading="lazy"
                />
              )}
              <span className="term">{supplierName}</span>
            </Link>
          );
        })}
      </div>
    );
  };

  const renderHomepageDropdown = () => {
    const suppliers = autoCompleteResults?.companyList;
    if (isEmpty(suppliers)) {
      return;
    }

    return (
      <div className="search-dropdown">
        {!isEmpty(suppliers) && (
          <div className="dropdown-section">
            <div className="section-header">
              <span>Relevant suppliers</span>
            </div>
            {suppliersInnerEl(take(suppliers, 5))}
            <div className="section-link" onClick={() => updateSearchQueryParam(currentSearchText)}>
              <span>See all results for &quot;{currentSearchText}&quot;</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBrowseMarketplaceDropdown = () => {
    let suppliers = [];
    let terms = [];
    if (isNull(autoCompleteResults)) {
      suppliers = defaultPopularSuppliers;
      terms = topRankedSearchTerms;
    } else {
      suppliers = autoCompleteResults?.companyList;
      terms = autoCompleteResults?.list;
    }

    if (isEmpty(terms) && isEmpty(suppliers)) {
      return;
    }

    return (
      <div className="search-dropdown">
        {!isEmpty(terms) && (
          <div className="dropdown-section">
            <div className="section-header">
              <span>Common Search Terms</span>
            </div>
            {searchTermsInnerEl(take(terms, 5))}
          </div>
        )}
        {!isEmpty(suppliers) && (
          <div className="dropdown-section">
            <div className="section-header">
              <span>Popular Suppliers</span>
            </div>
            {suppliersInnerEl(take(suppliers, 5))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="marketplace-search">
      <div className={cN('inner', { active: isDropdownVisible })}>
        <label>
          <Input
            ref={inputRef}
            placeholder={placeholderText}
            value={currentSearchText}
            onFocus={handleClickInputField}
            onBlur={() => window.setTimeout(() => setIsDropdownVisible(false), 200)}
            onChange={(e) => handleOnInputChange(e.target.value)}
            onPressEnter={() => handleSearch(currentSearchText)}
            aria-label="search sourcing input"
          />
          {!isEmpty(searchKey) && (
            <span className="clear-search" onClick={() => handleClearSearch()}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/close.png" width={10} height={10} alt="close icon" loading="lazy" />
            </span>
          )}
        </label>
        <div className="search-icon" onClick={() => handleSearch(currentSearchText)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/sourcing/search.png" width={12} height={12} alt="search icon" loading="lazy" />
        </div>
        {isDropdownVisible && searchType === SourcingMarketplaceSearchType.HOMEPAGE && renderHomepageDropdown()}
        {isDropdownVisible &&
          searchType === SourcingMarketplaceSearchType.BROWSE_MARKETPLACE &&
          renderBrowseMarketplaceDropdown()}
      </div>

      {isMobile && ifShowMobileFilterIcon && <FilterIcon className="filter-icon" onClick={onMobileFilterIconClick} />}
      {isDropdownVisible && <div className="outside-click-target" onClick={() => setIsDropdownVisible(false)} />}
    </div>
  );
};

export default SourcingMarketplaceSearch;
